// TMDB enrichment (§11). One IndexedDB-backed cache keyed on the Letterboxd
// URI; misses cached too.
//
// Real vs mock: in production (not test mode) we hit real TMDB whenever a key is
// present — that's the normal path, powering real genres/directors/posters/hot
// takes. In test mode we use a deterministic mock so screenshots and unit tests
// stay pixel-stable (§13.4), unless REAL_NETWORK=1 forces real for manual checks.

import { TMDB_API_KEY, REAL_NETWORK, IS_TEST } from "./env.ts";

/** Use real TMDB outside test mode when a key exists; in tests only if forced. */
const USE_REAL_TMDB = !!TMDB_API_KEY && (!IS_TEST || REAL_NETWORK);

export interface TmdbMeta {
  tmdbId: number | null;
  posterPath: string | null;
  popularity: number;
  voteAverage: number; // 0..10
  voteCount: number;
  genres: string[];
  directors: string[];
}

const GENRES = [
  "Drama",
  "Thriller",
  "Comedy",
  "Horror",
  "Sci-Fi",
  "Romance",
  "Crime",
  "Action",
  "Documentary",
  "Animation",
];
const DIRECTORS = [
  "Denis Villeneuve",
  "Greta Gerwig",
  "Bong Joon-ho",
  "Céline Sciamma",
  "David Fincher",
  "Ari Aster",
  "Christopher Nolan",
  "Charlotte Wells",
  "Paul Thomas Anderson",
  "Lynne Ramsay",
];

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic mock metadata from the film key (stable across runs). */
export function mockMeta(key: string, title: string): TmdbMeta {
  const h = hash(key + title);
  // Use unsigned shifts (>>>) — a signed >> would go negative for large hashes
  // and produce out-of-range (undefined) array lookups.
  const voteAverage = Math.round((1 + (h % 90) / 10) * 10) / 10; // 1.0..9.9
  const voteCount = 20 + (h % 4000);
  const popularity = Math.round(((h >>> 3) % 10000) / 10);
  const g1 = GENRES[h % GENRES.length];
  const g2 = GENRES[(h >>> 5) % GENRES.length];
  const genres = g1 === g2 ? [g1] : [g1, g2];
  const director = DIRECTORS[(h >>> 7) % DIRECTORS.length];
  return {
    tmdbId: h % 1000000,
    posterPath: null, // typographic placeholder poster is used when null
    popularity,
    voteAverage,
    voteCount,
    genres,
    directors: [director],
  };
}

// ---- IndexedDB cache -------------------------------------------------------

const DB_NAME = "twoshot";
const STORE = "tmdb";
// Bump when the meta shape or its source changes so stale entries are dropped.
// v2: switched from mock to real TMDB in production — old mock rows must not
// survive, or Deep focus would keep showing hash-derived genres/directors.
const DB_VERSION = 2;
let dbPromise: Promise<IDBDatabase | null> | null = null;
const memCache = new Map<string, TmdbMeta | null>();

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Drop any prior cache (e.g. mock-era rows) and recreate the store clean.
      if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
      db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

async function idbGet(key: string): Promise<TmdbMeta | null | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result as TmdbMeta | null | undefined);
    req.onerror = () => resolve(undefined);
  });
}

async function idbSet(key: string, value: TmdbMeta | null): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

// TMDB genre id -> name, so the search endpoint's genre_ids become usable names
// without a second details call. Directors still need /movie/{id}/credits and
// are left empty in the fast path.
const TMDB_GENRES: Record<number, string> = {
  28: "Action",
  12: "Adventure",
  16: "Animation",
  35: "Comedy",
  80: "Crime",
  99: "Documentary",
  18: "Drama",
  10751: "Family",
  14: "Fantasy",
  36: "History",
  27: "Horror",
  10402: "Music",
  9648: "Mystery",
  10749: "Romance",
  878: "Sci-Fi",
  10770: "TV Movie",
  53: "Thriller",
  10752: "War",
  37: "Western",
};

async function realLookup(title: string, year?: number): Promise<TmdbMeta | null> {
  const params = new URLSearchParams({ query: title, api_key: TMDB_API_KEY });
  if (year) params.set("year", String(year));
  const res = await fetch(`https://api.themoviedb.org/3/search/movie?${params}`);
  if (!res.ok) return null;
  const data = await res.json();
  const hit = data.results?.[0];
  if (!hit) return null;
  const genres: string[] = Array.isArray(hit.genre_ids)
    ? hit.genre_ids.map((id: number) => TMDB_GENRES[id]).filter((g: string | undefined): g is string => !!g)
    : [];

  return {
    tmdbId: hit.id,
    posterPath: hit.poster_path ? `https://image.tmdb.org/t/p/w342${hit.poster_path}` : null,
    popularity: hit.popularity ?? 0,
    voteAverage: hit.vote_average ?? 0,
    voteCount: hit.vote_count ?? 0,
    genres,
    // Directors need a second /credits call; only fetched when Deep focus is on.
    directors: [],
  };
}


/** Extra detail for a "feeling lucky" suggestion: poster + blurb from TMDB. */
export interface TmdbDetail {
  tmdbId: number | null;
  posterPath: string | null;
  overview: string;
  voteAverage: number;
  releaseYear: number | null;
  genres: string[];
}

/**
 * Look up a title (for the suggestion card) — poster, overview, rating. Returns
 * null when TMDB is unavailable (tests/mock or a miss); the card still renders
 * from the LLM's own reason line.
 */
export async function lookupTitle(title: string, year?: number): Promise<TmdbDetail | null> {
  if (!USE_REAL_TMDB) return null;
  try {
    const params = new URLSearchParams({ query: title, api_key: TMDB_API_KEY });
    if (year) params.set("year", String(year));
    const res = await fetch(`https://api.themoviedb.org/3/search/movie?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    const hit = data.results?.[0];
    if (!hit) return null;
    const genres: string[] = Array.isArray(hit.genre_ids)
      ? hit.genre_ids.map((id: number) => TMDB_GENRES[id]).filter((g: string | undefined): g is string => !!g)
      : [];
    const rel: string | undefined = hit.release_date;
    return {
      tmdbId: hit.id ?? null,
      posterPath: hit.poster_path ? `https://image.tmdb.org/t/p/w342${hit.poster_path}` : null,
      overview: typeof hit.overview === "string" ? hit.overview : "",
      voteAverage: hit.vote_average ?? 0,
      releaseYear: rel && rel.length >= 4 ? Number(rel.slice(0, 4)) : (year ?? null),
      genres,
    };
  } catch {
    return null;
  }
}

/** A streaming service a title is available on, with its logo. */
export interface WatchProvider {
  name: string;
  logoUrl: string | null;
}
export interface WatchInfo {
  /** ISO country code the availability is for (e.g. "US"). */
  region: string;
  /** JustWatch deep link TMDB provides for the region, if any. */
  link: string | null;
  /** Subscription/streaming providers ("flatrate"). */
  flatrate: WatchProvider[];
}

/** Viewer's country from the browser locale ("en-US" -> "US"), default US. */
export function viewerRegion(): string {
  if (typeof navigator === "undefined") return "US";
  const loc = navigator.language || "en-US";
  const parts = loc.split("-");
  return (parts[1] || "US").toUpperCase();
}

/**
 * Where a title streams, for the viewer's region. Returns null when TMDB is
 * unavailable or the film has no listed streaming option — the card just omits
 * the row. Data via TMDB (JustWatch); not availability we host.
 */
export async function watchProviders(tmdbId: number, region = viewerRegion()): Promise<WatchInfo | null> {
  if (!USE_REAL_TMDB || !tmdbId) return null;
  try {
    const res = await fetch(
      `https://api.themoviedb.org/3/movie/${tmdbId}/watch/providers?api_key=${TMDB_API_KEY}`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    const r = data.results?.[region] ?? data.results?.US;
    if (!r) return null;
    const flatrate: WatchProvider[] = Array.isArray(r.flatrate)
      ? r.flatrate.map((p: { provider_name: string; logo_path?: string }) => ({
          name: p.provider_name,
          logoUrl: p.logo_path ? `https://image.tmdb.org/t/p/w45${p.logo_path}` : null,
        }))
      : [];
    if (!flatrate.length) return null;
    return { region: data.results?.[region] ? region : "US", link: r.link ?? null, flatrate };
  } catch {
    return null;
  }
}

/** Enrich a single film, using the cache. Never throws — misses return null. */
export async function enrichFilm(uri: string, title: string, year?: number): Promise<TmdbMeta | null> {
  if (memCache.has(uri)) return memCache.get(uri)!;
  const cached = await idbGet(uri);
  if (cached !== undefined) {
    memCache.set(uri, cached);
    return cached;
  }
  let meta: TmdbMeta | null;
  if (USE_REAL_TMDB) {
    try {
      meta = await realLookup(title, year);
    } catch {
      meta = null;
    }
  } else {
    meta = mockMeta(uri, title);
  }
  memCache.set(uri, meta);
  await idbSet(uri, meta);
  return meta;
}

/** Clear the in-memory cache (tests). */
export function _resetMemCache() {
  memCache.clear();
}
